import { Workflow } from "lucide-react";
import GlassContainer from "@/shared/components/glasscontainer";
import { AutomationCard } from "./automation_card";
import type AutomationSummary from "../models/AutomationSummary";

export interface AutomationsGridProps {
  automations: AutomationSummary[];
  loading: boolean;
  error: Error | null;
  runningWorkflowId: string | null;
  deletingWorkflowId: string | null;
  onEdit: (workflowId: string) => void;
  onRun: (workflowId: string) => void;
  onDelete: (automation: AutomationSummary) => void;
  onShare: (automation: AutomationSummary) => void;
  /** Sacar del workspace. Solo lo pasa la vista de un workspace propio. */
  onUnfile?: (automation: AutomationSummary) => void;
  /** Elegir workspace para una automatización. Solo lo pasa la lista general. */
  onMoveToWorkspace?: (automation: AutomationSummary) => void;
  /** Texto del estado vacío, distinto según se liste todo o el contenido de un workspace. */
  emptyMessage?: string;
  /** Botón del estado vacío, para que un workspace recién creado no sea un callejón sin salida. */
  emptyAction?: { label: string; onClick: () => void };
}

export function AutomationsGrid({
  automations,
  loading,
  error,
  runningWorkflowId,
  deletingWorkflowId,
  onEdit,
  onRun,
  onDelete,
  onShare,
  onUnfile,
  onMoveToWorkspace,
  emptyMessage,
  emptyAction,
}: AutomationsGridProps) {
  if (error) {
    return (
      <GlassContainer className="p-6 mb-6 border-l-4 border-red-500 bg-red-500/5">
        <p className="text-red-700 font-medium">Error al cargar automatizaciones</p>
        <p className="text-red-600/80 text-sm mt-1">{error.message}</p>
      </GlassContainer>
    );
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <GlassContainer key={i} className="h-48 animate-pulse bg-white/20" />
        ))}
      </div>
    );
  }

  if (automations.length === 0) {
    return (
      <GlassContainer className="p-12 flex flex-col items-center justify-center text-center">
        <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center mb-4">
          <Workflow className="w-8 h-8 text-accent/50" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Aún no hay automatizaciones</h3>
        <p className="text-sm text-foreground/50 max-w-md">
          {emptyMessage ??
            "Crea tu primera automatización para empezar a conectar nodos en un lienzo visual."}
        </p>
        {emptyAction && (
          <button
            onClick={emptyAction.onClick}
            className="mt-6 cursor-pointer rounded-xl bg-accent px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-accent/25 transition-all hover:bg-accent/90"
          >
            {emptyAction.label}
          </button>
        )}
      </GlassContainer>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {automations.map((automation) => (
        <AutomationCard
          key={automation.workflowId}
          automation={automation}
          running={runningWorkflowId === automation.workflowId}
          deleting={deletingWorkflowId === automation.workflowId}
          onEdit={() => onEdit(automation.workflowId)}
          onRun={() => onRun(automation.workflowId)}
          onDelete={() => onDelete(automation)}
          onShare={() => onShare(automation)}
          onUnfile={onUnfile ? () => onUnfile(automation) : undefined}
          onMoveToWorkspace={onMoveToWorkspace ? () => onMoveToWorkspace(automation) : undefined}
        />
      ))}
    </div>
  );
}
